import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, resourceUrl } from '../../lib/utils';
import Seo from '../../components/Seo';
import ShareButtons from '../../components/ShareButtons';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SaveToFolderButton from '../../components/SaveToFolderButton';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';
import LibraryFreeNote from '../../components/LibraryFreeNote';
import SubmitResourceModal from '../../components/SubmitResourceModal';
import UploadResourceListModal from '../../components/UploadResourceListModal';
import ResourceStateLanding from './ResourceStateLanding';
import ResourceCategoryLanding from './ResourceCategoryLanding';
import { SEO_CATEGORY_SLUGS, SEO_CATEGORIES } from './communitySeoData';
import FlyerViewer from '../../components/FlyerViewer';
import { useAuth } from '../../context/AuthContext';
import { useResourcePacket } from '../../context/ResourcePacketContext';
import { toast } from 'sonner';
import {
  Search, MapPin, Phone, Globe, HeartHandshake, FolderOpen, X, Navigation,
  Apple, Home, Bus, Zap, ShieldAlert, Brain, HeartPulse, Users, PawPrint,
  Accessibility, Medal, Rainbow, Scale, FileText, Plus, Check, ArrowLeft,
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1713946598698-0fe798465d4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

// Branded 16:6 category banners (emerald/cream/gold, no text) — mapped by category icon family
const CATEGORY_BANNERS = {
  Apple: '/resource-banners/Apple.jpg',
  Home: '/resource-banners/Home.jpg',
  Bus: '/resource-banners/Bus.jpg',
  Zap: '/resource-banners/Zap.jpg',
  ShieldAlert: '/resource-banners/ShieldAlert.jpg',
  Brain: '/resource-banners/Brain.jpg',
  HeartPulse: '/resource-banners/HeartPulse.jpg',
  Users: '/resource-banners/Users.jpg',
  Accessibility: '/resource-banners/Accessibility.jpg',
  Medal: '/resource-banners/Medal.jpg',
  Rainbow: '/resource-banners/Rainbow.jpg',
  Scale: '/resource-banners/Scale.jpg',
  HeartHandshake: '/resource-banners/HeartHandshake.jpg',
};

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

const ICONS = { Apple, Home, Bus, Zap, ShieldAlert, Brain, HeartPulse, Users, Accessibility, Medal, Rainbow, Scale, HeartHandshake, PawPrint };

const deslug = (s) => (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const ResourceDirectory = ({ fixedCategory = null, seoTitle = null, seoDesc = null, pathState = null, pathCity = null, pathZip = null }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const packet = useResourcePacket();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [total, setTotal] = useState(0);
  const [statsTotal, setStatsTotal] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/content-stats`).then((r) => setStatsTotal(r.data.resources_total ?? 0)).catch(() => {});
  }, []);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get('search') || '');
  const [stateInput, setStateInput] = useState(params.get('state') || '');

  const category = fixedCategory || params.get('category') || '';
  const search = params.get('search') || '';
  const state = params.get('state') || '';
  const city = params.get('city') || '';
  const county = params.get('county') || '';
  const zip = params.get('zip') || '';
  const lat = params.get('lat') || '';
  const lng = params.get('lng') || '';
  const radius = params.get('radius') || '';
  const page = parseInt(params.get('page') || '1', 10);
  const [zipInput, setZipInput] = useState(zip);
  const [radiusInput, setRadiusInput] = useState(radius || '50');
  const [cityInput, setCityInput] = useState(city);
  const [countyInput, setCountyInput] = useState(county);
  const [locating, setLocating] = useState(false);
  const [catQuery, setCatQuery] = useState('');
  const [showCatMenu, setShowCatMenu] = useState(false);

  const selectedSlugs = category ? category.split(',').filter(Boolean) : [];

  useEffect(() => {
    document.title = seoTitle || 'Community Resources for Social Workers | INASW';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.content = seoDesc || 'A comprehensive, searchable directory of trusted community resources — food, housing, mental health, veterans, legal aid, and more. Find help. Share hope. Strengthen communities.';
  }, [seoTitle, seoDesc]);

  useEffect(() => {
    axios.get(`${API_URL}/resources/categories`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  // Seed filters from clean path URLs (/community-resources/texas/temple, /community-resources/76504)
  useEffect(() => {
    if (!pathState && !pathCity && !pathZip) return;
    const p = new URLSearchParams(params);
    let changed = false;
    if (pathZip && !p.get('zip')) { p.set('zip', pathZip); changed = true; }
    if (pathState && !p.get('state')) { p.set('state', deslug(pathState)); changed = true; }
    if (pathCity && !p.get('city')) { p.set('city', deslug(pathCity)); changed = true; }
    if (changed) { setParams(p, { replace: true }); setStateInput(p.get('state') || ''); setCityInput(p.get('city') || ''); setZipInput(p.get('zip') || ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathState, pathCity, pathZip]);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (category) q.set('category', category);
      if (search) q.set('search', search);
      if (state) q.set('state', state);
      if (city) q.set('city', city);
      if (county) q.set('county', county);
      if (zip) q.set('zip', zip);
      if (lat && lng) { q.set('lat', lat); q.set('lng', lng); }
      if (radius) q.set('radius', radius);
      q.set('page', page);
      const res = await axios.get(`${API_URL}/resources?${q.toString()}`);
      setResources(res.data.resources || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) { setResources([]); } finally { setLoading(false); }
  }, [category, search, state, city, county, zip, lat, lng, radius, page]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    if (key !== 'page') p.delete('page');
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (searchInput) p.set('search', searchInput); else p.delete('search');
    if (stateInput) p.set('state', stateInput); else p.delete('state');
    if (countyInput) p.set('county', countyInput); else p.delete('county');
    if (cityInput) p.set('city', cityInput); else p.delete('city');
    if (zipInput) { p.set('zip', zipInput); p.set('radius', radiusInput || '50'); p.delete('lat'); p.delete('lng'); } else { p.delete('zip'); }
    p.delete('page');
    setParams(p);
  };

  const toggleCat = (slug) => {
    const next = selectedSlugs.includes(slug)
      ? selectedSlugs.filter((s) => s !== slug)
      : [...selectedSlugs, slug];
    setParam('category', next.join(','));
    setCatQuery('');
    setShowCatMenu(false);
  };

  const matchingCats = (() => {
    const term = catQuery.trim().toLowerCase();
    const pool = categories.filter((c) => !selectedSlugs.includes(c.slug));
    if (!term) return pool.slice(0, 8);
    return pool
      .filter((c) => c.label.toLowerCase().includes(term) || (c.synonyms || []).some((s) => s.toLowerCase().includes(term)))
      .slice(0, 10);
  })();

  const nearMe = () => {
    if (!navigator.geolocation) { toast.error('Location is not supported on this device.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = new URLSearchParams(params);
        p.set('lat', pos.coords.latitude.toFixed(4));
        p.set('lng', pos.coords.longitude.toFixed(4));
        p.set('radius', radiusInput || '50');
        p.delete('zip'); p.delete('page');
        setParams(p); setZipInput(''); setLocating(false);
        toast.success('Showing resources near your location.');
      },
      () => { setLocating(false); toast.error('Could not get your location. Try entering a ZIP instead.'); },
      { timeout: 10000 }
    );
  };

  const activeCat = categories.find((c) => c.slug === category);

  const addToPacket = (r) => {
    if (!isAuthenticated) { toast.error('Please sign in to build a resource packet.'); navigate('/login'); return; }
    if (packet.inPacket(r.resource_id)) { packet.removeItem(r.resource_id); toast('Removed from packet'); }
    else { packet.addItem(r); toast.success('Added to packet'); }
  };

  const hasLocation = !!(state || city || county || zip || (lat && lng));
  const TIER_META = [
    { key: 'near_you', label: 'Near You', hint: 'Serving your city / ZIP' },
    { key: 'county', label: 'Serving Your County', hint: 'County & regional programs' },
    { key: 'statewide', label: 'Available Throughout the State', hint: 'Statewide programs' },
    { key: 'nationwide', label: 'Nationwide Assistance', hint: 'National programs available to you' },
  ];
  const groupedTiers = TIER_META
    .map((t) => ({ ...t, items: resources.filter((r) => (r._tier || 'nationwide') === t.key) }))
    .filter((t) => t.items.length > 0);

  const renderCard = (r) => (
    <div key={r.resource_id} className="bg-white rounded-2xl border p-5 flex flex-col" data-testid={`resource-${r.resource_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="inline-block text-xs font-semibold text-secondary uppercase tracking-wide">{r.category}</span>
          <h4 className="font-serif text-lg font-bold text-primary leading-tight">
            <Link to={resourceUrl(r)} className="hover:underline" data-testid={`resource-link-${r.resource_id}`}>{r.name}</Link>
          </h4>
        </div>
        {r.is_national && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex-shrink-0">Nationwide</span>}
      </div>
      <p className="text-sm text-gray-600 mt-2 flex-1">{r.description}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm">
        {r.phone && <a href={`tel:${r.phone.replace(/[^0-9]/g, '')}`} className="text-primary flex items-center gap-1 hover:underline"><Phone className="w-3.5 h-3.5" /> {r.phone}</a>}
        {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1 hover:underline"><Globe className="w-3.5 h-3.5" /> Website</a>}
        {(r.city || r.state) && <span className="text-gray-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[r.city, r.state].filter(Boolean).join(', ')}</span>}
        {r._distance != null && <span className="text-secondary font-semibold" data-testid="resource-distance">{r._distance} mi away</span>}
      </div>
      <div className="mt-4 pt-3 border-t flex justify-between items-center gap-2">
        <Button size="sm" variant={packet.inPacket(r.resource_id) ? 'secondary' : 'outline'}
          onClick={() => addToPacket(r)} data-testid={`add-packet-${r.resource_id}`}
          className={packet.inPacket(r.resource_id) ? 'bg-primary/10 text-primary hover:bg-primary/15' : ''}>
          {packet.inPacket(r.resource_id)
            ? (<><Check className="w-3.5 h-3.5 mr-1" /> In Packet</>)
            : (<><Plus className="w-3.5 h-3.5 mr-1" /> Add to Packet</>)}
        </Button>
        <SaveToFolderButton resourceId={r.resource_id} />
        <SaveToLibraryButton item={{ item_type: 'resource', ref_id: r.resource_id, title: r.name, subtitle: [r.city, r.state].filter(Boolean).join(', '), category: r.category, url: r.website || '' }} />
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <Seo
        {...(seoTitle || params.get('state')
          ? { title: seoTitle || `Community Resources in ${params.get('state')}${params.get('city') ? `, ${params.get('city')}` : ''}` }
          : { exactTitle: 'Community Resources for Social Workers | INASW' })}
        description={seoDesc || 'Community resources for social workers and clients nationwide — food, housing, mental health, veterans, legal aid and more. Search by state, city or ZIP. Continuously updated and link-checked.'}
        path="/community/resources"
      />
      {/* Floating packet bar */}
      {isAuthenticated && packet.count > 0 && (
        <div className="fixed bottom-5 right-5 z-50" data-testid="packet-floating-bar">
          <Button onClick={() => navigate('/community/resources/packet')} className="bg-primary hover:bg-primary/90 text-white shadow-xl h-12 px-5 rounded-full" data-testid="view-packet-btn">
            <FileText className="w-5 h-5 mr-2" /> Resource Packet
            <span className="ml-2 bg-secondary text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">{packet.count}</span>
          </Button>
        </div>
      )}
      {/* Hero */}
      <section className="relative bg-primary pt-24 pb-14 overflow-hidden" data-testid="resource-hero">
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
          <img src={HERO_IMG} alt="Social worker using the community resource directory" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl text-white">
            <p className="inline-flex items-center gap-2 text-secondary font-semibold tracking-widest uppercase text-xs mb-3"><HeartHandshake className="w-4 h-4" /> Built for Social Workers</p>
            {fixedCategory ? (
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">{activeCat?.label || 'Community Resources'}</h1>
            ) : (
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">National Social Worker <span className="text-secondary">Community Resource</span> Directory<sup className="align-super text-lg sm:text-xl ml-0.5">™</sup></h1>
            )}
            <p className="mt-4 text-white/90 text-lg">{fixedCategory ? `Find trusted ${(activeCat?.label || 'community').toLowerCase()} resources — search by ZIP code to surface local help plus nationwide services.` : 'Choose a resource type and your state to find trusted help for individuals, families, and communities.'}</p>
            <p className="mt-2 text-secondary font-semibold text-lg">Find help. Share hope. Strengthen communities.</p>
            {!fixedCategory && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="hero-submit-resource">
                <SubmitResourceModal categories={categories} triggerClassName="bg-white text-primary hover:bg-white/90 font-semibold" />
                <UploadResourceListModal categories={categories} isAdmin={user?.user_type === 'admin'} triggerClassName="bg-secondary hover:bg-secondary-hover text-white font-semibold" />
              </div>
            )}
            {fixedCategory && (
              <div className="mt-5 rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/10 max-w-xl" data-testid="category-banner">
                <img
                  src={CATEGORY_BANNERS[activeCat?.icon] || CATEGORY_BANNERS.HeartHandshake}
                  alt={`${activeCat?.label || 'Community'} resources`}
                  className="w-full h-[130px] md:h-[180px] object-cover"
                  loading="eager"
                />
              </div>
            )}
            <form onSubmit={submitSearch} className="mt-6 space-y-2 max-w-xl">
              {!fixedCategory && (
                <>
                  {/* Category multi-select autocomplete */}
                  <div className="relative">
                    {selectedSlugs.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="selected-categories">
                        {selectedSlugs.map((slug) => {
                          const c = categories.find((x) => x.slug === slug);
                          return (
                            <span key={slug} className="inline-flex items-center gap-1 bg-secondary text-white text-xs font-semibold rounded-full pl-3 pr-1.5 py-1" data-testid={`chip-${slug}`}>
                              {c?.label || slug}
                              <button type="button" onClick={() => toggleCat(slug)} className="hover:bg-white/20 rounded-full p-0.5" data-testid={`chip-remove-${slug}`}>
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        className="pl-9 bg-white text-gray-900"
                        placeholder="Type a resource type (e.g. food, rent, veterans, crisis)…"
                        value={catQuery}
                        onChange={(e) => { setCatQuery(e.target.value); setShowCatMenu(true); }}
                        onFocus={() => setShowCatMenu(true)}
                        onBlur={() => setTimeout(() => setShowCatMenu(false), 150)}
                        data-testid="category-autocomplete-input"
                      />
                    </div>
                    {showCatMenu && matchingCats.length > 0 && (
                      <div className="absolute z-30 mt-1 w-full bg-white rounded-lg shadow-xl border max-h-72 overflow-y-auto" data-testid="category-autocomplete-menu">
                        {matchingCats.map((c) => {
                          const Icon = ICONS[c.icon] || HeartHandshake;
                          return (
                            <button
                              type="button"
                              key={c.slug}
                              onMouseDown={(e) => { e.preventDefault(); toggleCat(c.slug); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-800 text-sm hover:bg-primary/5"
                              data-testid={`cat-option-${c.slug}`}
                            >
                              <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                              <span className="flex-1">{c.label}</span>
                              {c.count > 0 && <span className="text-xs text-gray-400">{c.count}</span>}
                              <Plus className="w-3.5 h-3.5 text-secondary" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Cascading location: State -> County -> City */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select className="bg-white text-gray-900 rounded-md px-3 h-10 text-sm border" value={stateInput} onChange={(e) => { setStateInput(e.target.value); setParam('state', e.target.value); }} data-testid="resource-state-select">
                      <option value="">All states</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Input className="bg-white text-gray-900" placeholder="County (optional)" value={countyInput} onChange={(e) => setCountyInput(e.target.value)} data-testid="resource-county-input" />
                    <Input className="bg-white text-gray-900" placeholder="City (optional)" value={cityInput} onChange={(e) => setCityInput(e.target.value)} data-testid="resource-city-input" />
                  </div>
                </>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input className="pl-9 bg-white text-gray-900" placeholder="Keyword search (e.g. food, shelter, crisis)" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="resource-search-input" />
                </div>
                <Button type="submit" className="bg-secondary hover:bg-secondary-hover text-white" data-testid="resource-search-btn">Search</Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input className="bg-white text-gray-900 sm:w-36" placeholder="ZIP code" value={zipInput} onChange={(e) => setZipInput(e.target.value)} data-testid="resource-zip-input" />
                <select className="bg-white text-gray-900 rounded-md px-3 h-10 text-sm sm:w-40 border" value={radiusInput} onChange={(e) => setRadiusInput(e.target.value)} data-testid="resource-radius-select">
                  <option value="10">Within 10 miles</option>
                  <option value="25">Within 25 miles</option>
                  <option value="50">Within 50 miles</option>
                  <option value="100">Within 100 miles</option>
                </select>
                <Button type="button" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" onClick={nearMe} disabled={locating} data-testid="near-me-btn">
                  <Navigation className="w-4 h-4 mr-2" /> {locating ? 'Locating…' : 'Near me'}
                </Button>
              </div>
              {(zip || (lat && lng)) && (
                <p className="text-white/80 text-xs">Showing local resources within {radius || 50} miles{zip ? ` of ${zip}` : ' of your location'}, plus nationwide services.</p>
              )}
            </form>
            {isAuthenticated && (
              <Link to="/my-referral-folders"><Button variant="outline" className="mt-4 border-white text-white hover:bg-white hover:text-primary" data-testid="my-folders-link"><FolderOpen className="w-4 h-4 mr-2" /> My Referral Folders</Button></Link>
            )}
            {user?.user_type === 'admin' && (
              <Link to="/admin/resources"><Button variant="outline" className="mt-4 ml-2 border-secondary text-secondary hover:bg-secondary hover:text-white" data-testid="admin-manage-resources">Manage Resources</Button></Link>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <LibraryFreeNote variant="banner" />
      </div>

      {/* Resource flyer — half size, clickable/enlarge/print/share, with live count */}
      {!fixedCategory && (
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-testid="resource-flyer">
          <div className="text-center mb-3">
            <span className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-4 py-1.5 text-sm font-semibold" data-testid="resource-count-badge">
              {statsTotal !== null ? statsTotal.toLocaleString() : '—'} resources listed
            </span>
            <p className="text-xs text-gray-400 mt-1">Refreshed automatically every morning at 8&nbsp;AM</p>
          </div>
          <FlyerViewer src="/flyer-community-resources.png" alt="INASW National Social Worker Community Resource Directory" />
        </section>
      )}

      {/* Popular category quick-picks (only those with listings) */}
      {!fixedCategory && categories.filter((c) => c.count > 0).length > 0 && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-serif text-2xl font-bold text-primary mb-1">Popular Categories</h2>
        <p className="text-gray-500 text-sm mb-4">Or use the dropdowns above to choose from all {categories.length} resource types.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {categories.filter((c) => c.count > 0).map((c) => {
            const Icon = ICONS[c.icon] || HeartHandshake;
            const active = selectedSlugs.includes(c.slug);
            return (
              <Link key={c.slug} to={`/community/resources/${c.slug}`} data-testid={`category-${c.slug}`}
                className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${active ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-secondary'}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-white/15' : 'bg-primary/10'}`}>
                  <Icon className={`w-4 h-4 ${active ? 'text-secondary' : 'text-primary'}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">{c.label}</p>
                  <p className={`text-xs ${active ? 'text-white/80' : 'text-secondary'}`}>Find Local &amp; State Resources</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-extrabold text-primary">
              {activeCat
                ? activeCat.label
                : selectedSlugs.length > 0
                  ? selectedSlugs.map((s) => categories.find((c) => c.slug === s)?.label || s).join(', ')
                  : 'All Resources'} <span className="text-primary font-extrabold">({total.toLocaleString()})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-1">Verified resources — continuously updated &amp; link-checked.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ShareButtons title="INASW Community Resources" text="Community resources on I Need A Social Worker" testidPrefix="results-share" />
            <SubmitResourceModal categories={categories} />
            {fixedCategory ? (
              <Link to="/community/resources" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1" data-testid="back-to-directory"><ArrowLeft className="w-4 h-4" /> All resource categories</Link>
            ) : (category || search || state || city || county || zip) && (
              <button onClick={() => setParams(new URLSearchParams())} className="text-sm text-gray-500 hover:text-primary flex items-center gap-1" data-testid="clear-filters"><X className="w-4 h-4" /> Clear filters</button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading resources…</div>
        ) : resources.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center text-gray-500" data-testid="no-resources">No resources match your search. Try clearing filters.</div>
        ) : hasLocation ? (
          <div className="space-y-8">
            {groupedTiers.map((t) => (
              <div key={t.key} data-testid={`tier-${t.key}`}>
                <div className="flex items-baseline gap-3 mb-3">
                  <h4 className="font-serif text-xl font-bold text-primary">{t.label}</h4>
                  <span className="text-xs text-gray-400">{t.hint} · {t.items.length}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {t.items.map(renderCard)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {resources.map(renderCard)}
          </div>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button variant="outline" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))} data-testid="prev-page">Previous</Button>
            <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {pages}</span>
            <Button variant="outline" disabled={page >= pages} onClick={() => setParam('page', String(page + 1))} data-testid="next-page">Next</Button>
          </div>
        )}
        <p className="text-xs text-gray-400 text-center mt-8">Information is subject to change without notice. Listing here is not an endorsement.</p>

        {/* Nationwide resource guides — internal links to state x category SEO pages */}
        <div className="mt-14 border-t border-gray-100 pt-10" data-testid="cr-guides-index">
          <h2 className="font-serif text-2xl font-bold text-gray-900">Community Resource Guides by State</h2>
          <p className="text-gray-600 mt-2 max-w-2xl">In-depth guides covering real programs, eligibility and how to apply — for all 50 states and Washington, DC.</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {SEO_CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/community-resources/${c.slug}`} className="bg-white rounded-lg border border-gray-100 px-4 py-3 text-sm font-medium text-gray-800 hover:border-primary hover:text-primary transition-colors" data-testid="cr-guide-link">
                {c.label} by State
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourceDirectory;

// Wrapper for clean path URLs: /community-resources/:seg1(/:seg2)
// seg1 = SEO category slug (nationwide guide) OR ZIP (5 digits) OR state slug; seg2 = state slug / city slug
export const ResourceDirectoryByPath = () => {
  const { seg1, seg2 } = useParams();
  if (SEO_CATEGORY_SLUGS.has(seg1)) {
    return seg2 ? <ResourceStateLanding /> : <ResourceCategoryLanding />;
  }
  const isZip = /^\d{5}$/.test(seg1 || '');
  return (
    <ResourceDirectory
      pathZip={isZip ? seg1 : null}
      pathState={!isZip ? seg1 : null}
      pathCity={!isZip ? seg2 : null}
    />
  );
};
